import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Observable, Subject, defer, of,  } from 'rxjs';
import { map, startWith, switchMap, take, tap } from 'rxjs/operators';
import { dirtyCheck } from '../../operators';

@Injectable({ providedIn: 'root' })
export class DirtyCheckService {
  /** Represent the dirty state of the registered dirtyCheck within the current activeRoute */
  private _isDirty$: Observable<boolean>;

  private _confirmUnsavedChanges$ = new Subject<boolean>();
  private _pendingUnsavedChangesToConfirmSub$ = new Subject<void>();

  /** Represents the pending unsaved changes confirmation. */
  public pendingUnsavedChangesToConfirm$ =
    this._pendingUnsavedChangesToConfirmSub$.asObservable();

  /**
   * Register a dirty check for the given form group.
   * @param formGroup The form group.
   * @param options The dirty check options.
   * * `deepEqual`: When false or not supplied (the default) the dirty check will use
   * the form's `dirty` property instead of the deep equal dirty check.
   */
  registerDirtyCheck(
    formGroup: FormGroup,
    options?: {
      deepEqual?: boolean;
    },
  ): void;
  /**
   * Register a dirty check for the given origin and changes observables.
   * @param origin The origin observable.
   * @param changes The changes observable.
   */
  registerDirtyCheck(origin: Observable<any>, changes: Observable<any>): void;
  registerDirtyCheck(arg1: any, arg2?: any): void {
    if (arg1 instanceof FormGroup) {
      const formGroup = arg1;
      const deepEqual = arg2?.deepEqual ?? false;

      // If the deepEqual option is not set, use the default dirty check.
      if (!deepEqual) {
        // Use defer to get the dirty state only when isDirty$ is subscribed.
        this._isDirty$ = defer(() => of(formGroup.dirty));
      } else {
        // If the deepEqual option is set, use the deep dirty check.
        const origin = of(formGroup.getRawValue());
        const changes = formGroup.valueChanges.pipe(
          startWith(true),
          map(() => formGroup.getRawValue()),
        );

        this._isDirty$ = this._getDeepDirtyCheck(origin, changes);
      }
    } else {
      const origin = arg1;
      const changes = arg2;

      this._isDirty$ = this._getDeepDirtyCheck(origin, changes);
    }
  }

  /** Skip the dirty state for the next navigation. */
  skipDirtyCheckForActivatedRoute() {
    this._isDirty$ = null;
  }

  /**
   * Permrorms a dirty check for the current activated route.
   * Checks if there are pending unsaved changes.
   * If there are, it will open a confirmation dialog. If the user confirms the changes, it will return `true`.
   * If the user cancels the changes, it will return `false`.
   * If there are no pending unsaved changes, it will return `true`.
   * @returns Observable of `true` if there are no pending unsaved changes, `false` otherwise.
   */
  performDirtyCheckForActivatedRoute(): Observable<boolean> {
    if (!this._isDirty$) {
      return of(true);
    }

    return this._isDirty$.pipe(
      take(1),
      switchMap((dirty) => {
        if (!dirty) {
          return of(true);
        }

        this._pendingUnsavedChangesToConfirmSub$.next();
        return this._confirmUnsavedChanges$.pipe(take(1));
      }),
      tap((confirmed) => {
        // Reset the dirty state if the user confirmed the changes.
        // Otherwise, keep the dirty state so that the user can confirm the changes again.
        if (confirmed) this._isDirty$ = null;
      }),
    );
  }

  /**
   * Confirm the pending unsaved changes.
   * @param confirmed `true` if the user confirmed the changes, `false` otherwise.
   */
  confirmUnsavedChanges(confirmed: boolean): void {
    this._confirmUnsavedChanges$.next(confirmed);
  }

  /** Get the deep dirty check for the given origin and changes observables. */
  private _getDeepDirtyCheck(
    origin: Observable<any>,
    changes: Observable<any>,
  ) {
    return changes.pipe(dirtyCheck(origin));
  }
}
